import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableContainer,
  Tag,
  InlineLoading,
  Button,
} from '@carbon/react';
import {
  Analytics,
  Time,
  CalendarHeatMap,
  Bullhorn,
  ArrowLeft,
} from '@carbon/react/icons';
import { useNavigate } from 'react-router-dom';

const MONTHLY_CAP = 15.0;

// ISO week number: returns "YYYY-WNN"
const getWeekKey = date => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const jan1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((d - jan1) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
};

const fmtCost = n => `$${(n ?? 0).toFixed(2)}`;
const fmtNum = n => (n ?? 0).toLocaleString();

const tableHeaders = [
  { key: 'date', header: 'Date' },
  { key: 'calls', header: 'Calls' },
  { key: 'input_tokens', header: 'Input Tokens' },
  { key: 'output_tokens', header: 'Output Tokens' },
  { key: 'cost', header: 'Cost' },
];

const UsagePage = ({ token }) => {
  const navigate = useNavigate();
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch('/mlapi/basic/billing/usage', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setUsageData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, '0')}`;
  const currentWeekKey = getWeekKey(now);
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(now.getDate()).padStart(2, '0')}`;

  const monthData = usageData?.[currentMonthKey];
  const weekData = usageData?.[currentWeekKey];
  const todayData = usageData?.[todayKey];
  const lastCallData = usageData?.['last_call'];

  const monthCost = monthData?.cost ?? 0;
  const progressPercent = Math.min((monthCost / MONTHLY_CAP) * 100, 100);
  const capStatus =
    progressPercent >= 90 ? 'red' : progressPercent >= 70 ? 'yellow' : 'green';
  const capTagType =
    capStatus === 'red'
      ? 'red'
      : capStatus === 'yellow'
      ? 'warm-gray'
      : 'green';
  const capLabel =
    capStatus === 'red'
      ? 'Near Limit'
      : capStatus === 'yellow'
      ? 'Moderate Use'
      : 'Good Standing';

  // Daily entries sorted newest first, last 14 days
  const dailyRows = usageData
    ? Object.entries(usageData)
        .filter(([k]) => /^\d{4}-\d{2}-\d{2}$/.test(k))
        .sort(([a], [b]) => b.localeCompare(a))
        .slice(0, 14)
        .map(([k, v]) => ({
          id: k,
          date: k,
          calls: fmtNum(v.calls),
          input_tokens: fmtNum(v.input_tokens),
          output_tokens: fmtNum(v.output_tokens),
          cost: fmtCost(v.cost),
        }))
    : [];

  if (loading) {
    return (
      <div className="usage-page">
        <div className="usage-loading">
          <InlineLoading description="Loading usage data..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="usage-page">
        <Button
          kind="ghost"
          renderIcon={ArrowLeft}
          onClick={() => navigate('/')}>
          Back to AI Assistant
        </Button>
        <p className="usage-error">Could not load usage data: {error}</p>
      </div>
    );
  }

  return (
    <div className="usage-page">
      <div className="usage-container">
        <Button
          kind="ghost"
          renderIcon={ArrowLeft}
          onClick={() => navigate('/')}
          className="usage-back-btn">
          Back to AI Assistant
        </Button>
        <div className="usage-header">
          <Analytics size={24} />
          <h2>AI Usage</h2>
        </div>
        <p className="usage-subtitle">
          Token consumption and costs for your account
        </p>

        {/* Monthly progress card */}
        <div className="usage-monthly-card">
          <div className="usage-monthly-top">
            <h4>This Month &mdash; {currentMonthKey}</h4>
            <Tag type={capTagType} size="sm">
              {capLabel}
            </Tag>
          </div>

          <div className="usage-cost-row">
            <span className="usage-cost-current">{fmtCost(monthCost)}</span>
            <span className="usage-cost-sep">/</span>
            <span className="usage-cost-cap">
              ${MONTHLY_CAP.toFixed(2)} monthly limit
            </span>
          </div>

          <div className="usage-progress-track">
            <div
              className={`usage-progress-fill usage-progress-${capStatus}`}
              style={{ width: `${progressPercent.toFixed(2)}%` }}
              role="progressbar"
              aria-valuenow={progressPercent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${progressPercent.toFixed(
                1
              )}% of monthly limit used`}
            />
          </div>
          <div className="usage-progress-labels">
            <span>{progressPercent.toFixed(1)}% used</span>
            <span>{(100 - progressPercent).toFixed(1)}% remaining</span>
          </div>

          {monthData && (
            <p className="usage-monthly-stats">
              {fmtNum(monthData.calls)} calls &middot;{' '}
              {fmtNum(monthData.input_tokens)} input tokens &middot;{' '}
              {fmtNum(monthData.output_tokens)} output tokens
            </p>
          )}

          {capStatus === 'red' && (
            <div className="usage-cap-warning">
              <Bullhorn size={16} />
              <span>
                You&apos;re approaching your monthly limit. Usage may be paused
                when the cap is reached.
              </span>
            </div>
          )}
        </div>

        {/* Summary tiles */}
        <Grid className="usage-tiles-grid" narrow>
          <Column lg={4} md={4} sm={4}>
            <Tile className="usage-tile">
              <div className="usage-tile-icon">
                <Time size={20} />
              </div>
              <p className="usage-tile-label">Last Call</p>
              {lastCallData ? (
                <>
                  <p className="usage-tile-cost">
                    {fmtCost(lastCallData.cost)}
                  </p>
                  <p className="usage-tile-detail">
                    {fmtNum(lastCallData.input_tokens)} in &middot;{' '}
                    {fmtNum(lastCallData.output_tokens)} out
                  </p>
                </>
              ) : (
                <p className="usage-tile-empty">No data</p>
              )}
            </Tile>
          </Column>

          <Column lg={4} md={4} sm={4}>
            <Tile className="usage-tile">
              <div className="usage-tile-icon">
                <CalendarHeatMap size={20} />
              </div>
              <p className="usage-tile-label">Today &mdash; {todayKey}</p>
              {todayData ? (
                <>
                  <p className="usage-tile-cost">{fmtCost(todayData.cost)}</p>
                  <p className="usage-tile-detail">
                    {fmtNum(todayData.calls)} calls &middot;{' '}
                    {fmtNum(todayData.input_tokens)} in &middot;{' '}
                    {fmtNum(todayData.output_tokens)} out
                  </p>
                </>
              ) : (
                <p className="usage-tile-empty">No activity today</p>
              )}
            </Tile>
          </Column>

          <Column lg={4} md={4} sm={4}>
            <Tile className="usage-tile">
              <div className="usage-tile-icon">
                <Analytics size={20} />
              </div>
              <p className="usage-tile-label">
                This Week &mdash; {currentWeekKey}
              </p>
              {weekData ? (
                <>
                  <p className="usage-tile-cost">{fmtCost(weekData.cost)}</p>
                  <p className="usage-tile-detail">
                    {fmtNum(weekData.calls)} calls &middot;{' '}
                    {fmtNum(weekData.input_tokens)} in &middot;{' '}
                    {fmtNum(weekData.output_tokens)} out
                  </p>
                </>
              ) : (
                <p className="usage-tile-empty">No data this week</p>
              )}
            </Tile>
          </Column>
        </Grid>

        {/* Daily breakdown table */}
        {dailyRows.length > 0 && (
          <div className="usage-table-section">
            <h4>Recent Daily Breakdown</h4>
            <DataTable rows={dailyRows} headers={tableHeaders} size="sm">
              {({
                rows,
                headers,
                getTableProps,
                getHeaderProps,
                getRowProps,
                getTableContainerProps,
              }) => (
                <TableContainer {...getTableContainerProps()}>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader
                            key={header.key}
                            {...getHeaderProps({ header })}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow key={row.id} {...getRowProps({ row })}>
                          {row.cells.map(cell => (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsagePage;
